import { match } from 'ts-pattern';
import { z } from 'zod';

import { EXAMPLE_TYPES, SOURCE_TYPES } from '@/types/evals';

// Step 1: General Info Schema
export const generalInfoSchema = z.object( {
  name: z.string().min( 1, 'Name is required' ),
  description: z.string().optional(),
  source_type: z.enum( [ SOURCE_TYPES.SNOWFLAKE, SOURCE_TYPES.JSON, SOURCE_TYPES.AGENT_ACTION_LOGS, SOURCE_TYPES.MODEL_INVOCATION_LOGS, SOURCE_TYPES.COMPLETION_LOGS ], { message: 'Please select a source type' } )
} );

// Snowflake Config Schema
export const snowflakeConfigSchema = z.object( {
  credential_ref: z.string().min( 1, 'Credential Reference is required' ),
  database_name: z.string().min( 1, 'Database Name is required' ),
  query: z.string().min( 1, 'Query is required' ),
  schema_name: z.string().optional()
} );

// Shared message validator for inline examples — used by both the source
// create wizard and the run-create modal. A message carries either textual
// content (`message`) or a structured payload (`data`), never both — mirrors
// the aidp-eval `Message` union (TextMessage | StructuredMessage). `user` is a
// non-empty string (not an enum) to match aidp-eval's `Message.user`, which is
// a plain `str` accepting arbitrary author labels; user/assistant turn
// ordering, where it matters, is enforced separately by the consuming example
// schema's refine.
//
// This is form-input validation (consumed by react-hook-form), so it stays a
// single loose object with a refine enforcing exactly-one rather than a
// transform-to-union (a transform makes the schema's input ≠ output, which
// breaks useForm's generics). The strict union is the read-side domain type.
export const exampleMessageSchema = z.object( {
  user: z.string().min( 1, 'User is required' ),
  message: z.string().min( 1, 'Message cannot be empty' ).optional(),
  data: z.unknown().optional()
} ).refine(
  ( m ) => ( m.message !== undefined ) !== ( m.data !== undefined ),
  { message: 'Each message must have exactly one of "message" (text) or "data" (structured)' }
);

const exampleSchema = z.object( {
  id: z.string().optional(),
  type: z.enum( [ EXAMPLE_TYPES.MESSAGE, EXAMPLE_TYPES.CONVERSATION ] ),
  data: z.array( exampleMessageSchema ).min( 1, 'At least one message is required' ),
  meta_data: z.record( z.string(), z.unknown() ).optional()
} );

// A single inline example as validated by the create schema (draft shape:
// messages are the loose text-or-structured form, not the strict read-side
// ExampleMessage union). "Inline" = supplied directly in the request, as
// opposed to examples loaded from a Snowflake/MAS source by an adapter.
// Shared with the JSON source editor.
export type InlineExample = z.infer<typeof exampleSchema>;

export const jsonConfigSchema = z.object( {
  examples: z.array( exampleSchema )
    .min( 1, 'At least one example is required' )
    .max( 1000, 'Maximum 1000 examples allowed' )
    .superRefine( ( examples, ctx ) => {
      // Validate that all examples have the same type
      if ( examples.length > 0 ) {
        const firstType = examples[ 0 ].type;
        const allSameType = examples.every( ( ex ) => ex.type === firstType );
        if ( !allSameType ) {
          ctx.addIssue( {
            code: z.ZodIssueCode.custom,
            message: 'All examples must have the same type',
            path: []
          } );
        }
      }
    } )
} );

const hasValue = ( v: unknown ) => v !== null && v !== undefined && v !== '';

type AgentFilters = { filters: { agent_id?: string | null; agent_name?: string | null; constellation_name?: string | null } };

const requireAtLeastOneFilter = ( data: { filters: Record<string, unknown> } ) => (
  Object.values( data.filters ).some( hasValue )
);

const agentIdMutualExclusivity = ( data: AgentFilters ) => {
  const hasAgentId = hasValue( data.filters.agent_id );
  const hasNames = hasValue( data.filters.agent_name ) || hasValue( data.filters.constellation_name );
  return !( hasAgentId && hasNames );
};

// agent_name is only meaningful within a constellation, so constellation_name is required when agent_name is set
const agentNameRequiresConstellation = ( data: AgentFilters ) => (
  !hasValue( data.filters.agent_name ) || hasValue( data.filters.constellation_name )
);

const AT_LEAST_ONE_FILTER_MSG = 'At least one filter must be provided';
const AGENT_ID_EXCLUSIVITY_MSG = 'agent_id cannot be combined with agent_name or constellation_name';
const AGENT_NAME_REQUIRES_CONSTELLATION_MSG = 'agent_name cannot be provided without constellation_name';

// Agent Action Logs Config Schema
export const agentActionLogsConfigSchema = z.object( {
  filters: z.object( {
    component_name: z.string().nullish(),
    constellation_name: z.string().nullish(),
    constellation_version: z.string().nullish(),
    client_id: z.string().nullish(),
    correlation_id: z.string().nullish(),
    request_id: z.string().nullish(),
    agent_name: z.string().nullish(),
    agent_id: z.string().nullish(),
    metadata: z.record( z.string(), z.unknown() ).nullish(),
    from_start_time: z.string().nullish(),
    to_end_time: z.string().nullish()
  } ),
  row_limit: z.number().int().positive().nullish()
} ).refine( requireAtLeastOneFilter, AT_LEAST_ONE_FILTER_MSG
).refine( agentIdMutualExclusivity, AGENT_ID_EXCLUSIVITY_MSG
).refine( agentNameRequiresConstellation, AGENT_NAME_REQUIRES_CONSTELLATION_MSG );

// Model Invocation Logs Config Schema
export const modelInvocationLogsConfigSchema = z.object( {
  filters: z.object( {
    agent_name: z.string().nullish(),
    agent_id: z.string().nullish(),
    model_id: z.string().nullish(),
    constellation_name: z.string().nullish(),
    constellation_version: z.string().nullish(),
    client_id: z.string().nullish(),
    correlation_id: z.string().nullish(),
    request_id: z.string().nullish(),
    requester_tags: z.array( z.string() ).nullish(),
    requester_metadata: z.record( z.string(), z.unknown() ).nullish(),
    from_start_time: z.string().nullish(),
    to_end_time: z.string().nullish()
  } ),
  row_limit: z.number().int().positive().nullish(),
  context_window: z.number().int().positive().nullish()
} ).refine( requireAtLeastOneFilter, AT_LEAST_ONE_FILTER_MSG
).refine( agentIdMutualExclusivity, AGENT_ID_EXCLUSIVITY_MSG
).refine( agentNameRequiresConstellation, AGENT_NAME_REQUIRES_CONSTELLATION_MSG );

// Request/Response Logs Config Schema (internal source type: mas_completion_log)
export const completionLogsConfigSchema = z.object( {
  filters: z.object( {
    agent_name: z.string().nullish(),
    agent_id: z.string().nullish(),
    constellation_name: z.string().nullish(),
    constellation_version: z.string().nullish(),
    client_id: z.string().nullish(),
    correlation_id: z.string().nullish(),
    request_id: z.string().nullish(),
    from_start_time: z.string().nullish(),
    to_end_time: z.string().nullish()
  } ),
  row_limit: z.number().int().positive().nullish()
} ).refine( requireAtLeastOneFilter, AT_LEAST_ONE_FILTER_MSG
).refine( agentIdMutualExclusivity, AGENT_ID_EXCLUSIVITY_MSG
).refine( agentNameRequiresConstellation, AGENT_NAME_REQUIRES_CONSTELLATION_MSG );

// Step 2: Source Type Config Schema - uses discriminated union based on sourceType
export const sourceTypeConfigSchema = z.object( {
  example_type: z.enum( [ EXAMPLE_TYPES.CONVERSATION, EXAMPLE_TYPES.MESSAGE ] ),
  source_type: z.enum( [ SOURCE_TYPES.SNOWFLAKE, SOURCE_TYPES.JSON, SOURCE_TYPES.AGENT_ACTION_LOGS, SOURCE_TYPES.MODEL_INVOCATION_LOGS, SOURCE_TYPES.COMPLETION_LOGS ] ),
  config: z.any() // We'll validate this dynamically based on sourceType
} );

// Full Form Schema with discriminated config validation
export const fullSourceFormSchema = z.object( {
  name: z.string().min( 1, 'Name is required' ),
  description: z.string().optional(),
  source_type: z.enum( [ SOURCE_TYPES.SNOWFLAKE, SOURCE_TYPES.JSON, SOURCE_TYPES.AGENT_ACTION_LOGS, SOURCE_TYPES.MODEL_INVOCATION_LOGS, SOURCE_TYPES.COMPLETION_LOGS ] ),
  example_type: z.enum( [ EXAMPLE_TYPES.CONVERSATION, EXAMPLE_TYPES.MESSAGE ] ),
  config: z.any()
} ).superRefine( ( data, ctx ) => {
  // Validate config based on sourceType
  match( data.source_type )
    .with( SOURCE_TYPES.SNOWFLAKE, () => {
      const result = snowflakeConfigSchema.safeParse( data.config );
      if ( !result.success ) {
        result.error.issues.forEach( ( issue ) => {
          ctx.addIssue( {
            ...issue,
            path: [ 'config', ...issue.path ]
          } );
        } );
      }
    } )
    .with( SOURCE_TYPES.JSON, () => {
      const result = jsonConfigSchema.safeParse( data.config );
      if ( !result.success ) {
        result.error.issues.forEach( ( issue ) => {
          ctx.addIssue( {
            ...issue,
            path: [ 'config', ...issue.path ]
          } );
        } );
      }
    } )
    .with( SOURCE_TYPES.AGENT_ACTION_LOGS, () => {
      const result = agentActionLogsConfigSchema.safeParse( data.config );
      if ( !result.success ) {
        result.error.issues.forEach( ( issue ) => {
          ctx.addIssue( {
            ...issue,
            path: [ 'config', ...issue.path ]
          } );
        } );
      }
    } )
    .with( SOURCE_TYPES.MODEL_INVOCATION_LOGS, () => {
      const result = modelInvocationLogsConfigSchema.safeParse( data.config );
      if ( !result.success ) {
        result.error.issues.forEach( ( issue ) => {
          ctx.addIssue( {
            ...issue,
            path: [ 'config', ...issue.path ]
          } );
        } );
      }
    } )
    .with( SOURCE_TYPES.COMPLETION_LOGS, () => {
      const result = completionLogsConfigSchema.safeParse( data.config );
      if ( !result.success ) {
        result.error.issues.forEach( ( issue ) => {
          ctx.addIssue( {
            ...issue,
            path: [ 'config', ...issue.path ]
          } );
        } );
      }
    } )
    .otherwise( () => {} );
} );

// Type inference
export type GeneralInfoFormData = z.infer<typeof generalInfoSchema>;
export type SnowflakeConfigFormData = z.infer<typeof snowflakeConfigSchema>;
export type JsonConfigFormData = z.infer<typeof jsonConfigSchema>;
export type AgentActionLogsConfigFormData = z.infer<typeof agentActionLogsConfigSchema>;
export type ModelInvocationLogsConfigFormData = z.infer<typeof modelInvocationLogsConfigSchema>;
export type CompletionLogsConfigFormData = z.infer<typeof completionLogsConfigSchema>;
export type SourceTypeConfigFormData = z.infer<typeof sourceTypeConfigSchema>;
export type FullSourceFormData = z.infer<typeof fullSourceFormSchema>;
